export const FINETUNE_ELEMENT_DESCRIPTIONS = Object.freeze({
  link: 'Cascading Styles Sheets',
  css: 'Cascading Styles Sheets',
  div: 'Divisions',
  href: 'Href Links',
  img: 'Images',
  image: 'Images',
  line_num: '# of HTML Lines',
  script: 'Javascripts',
  iframe: 'Iframes',
  form: 'Forms'
});

export const THRESHOLD_ELEMENT_TO_ATTRIBUTE = Object.freeze({
  href: 'threshold_href',
  img: 'threshold_img',
  image: 'threshold_img',
  script: 'threshold_script',
  div: 'threshold_div',
  iframe: 'threshold_iframe',
  form: 'threshold_form',
  link: 'threshold_link',
  line_num: 'threshold_line_num'
});

export function getFinetuneElementDescription(element) {
  return FINETUNE_ELEMENT_DESCRIPTIONS[element] || element;
}

/** Normalizes webpage tag payloads for the related-threshold apply control. */
export function normalizeRelatedTags(tags) {
  return (Array.isArray(tags) ? tags : [])
    .map((tag) => {
      const id = tag && (tag.id != null ? tag.id : tag.tagID);
      if (id == null) return null;
      return {
        id,
        name: (tag && (tag.name || tag.tag_name)) || `Tag ${id}`
      };
    })
    .filter(Boolean);
}

export function countThresholdEntries(urls) {
  return (Array.isArray(urls) ? urls : []).reduce(
    (total, entry) => total + Object.keys((entry && entry.thresholds) || {}).length,
    0
  );
}

/**
 * Parses a threshold input without coercing blank values to zero.
 *
 * @returns {number|null}
 */
export function parseThresholdInput(raw) {
  if (
    (typeof raw !== 'number' && typeof raw !== 'string') ||
    (typeof raw === 'string' && raw.trim() === '')
  ) {
    return null;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

const THRESHOLD_APPLY_SKIP_LABELS = Object.freeze({
  already_above_floor: 'URL already at or above the selected minimum',
  url_not_found: 'URL not found',
  url_archived: 'URL is archived',
  metric_disabled: 'URL, this threshold is disabled',
  forbidden: 'not allowed to update',
  unknown_element: 'unknown element',
  invalid_threshold: 'invalid threshold'
});

export function formatThresholdApplySkippedSummary(skipped) {
  const counts = (Array.isArray(skipped) ? skipped : []).reduce((result, row) => {
    const reason = (row && row.reason) || 'unknown';
    result[reason] = (result[reason] || 0) + 1;
    return result;
  }, {});

  return Object.keys(counts)
    .map((reason) => {
      const count = counts[reason];
      return `${count} ${THRESHOLD_APPLY_SKIP_LABELS[reason] || reason}`;
    })
    .join('; ');
}

export function hasOnlyThresholdFloorSkips(skipped) {
  return (
    Array.isArray(skipped) &&
    skipped.length > 0 &&
    skipped.every((row) => row && row.reason === 'already_above_floor')
  );
}

/**
 * Converts an apply response into the ordered notifications shown by every
 * threshold-apply screen.
 */
export function formatThresholdApplyResult(response = {}) {
  const applied = Array.isArray(response.applied) ? response.applied : [];
  const skipped = Array.isArray(response.skipped) ? response.skipped : [];
  const notifications = [];

  if (applied.length > 0) {
    const updatedUrlCount = new Set(
      applied.map((row) => row && row.url_id).filter((id) => id != null)
    ).size;
    notifications.push({
      type: 'success',
      message: `Applied ${applied.length} threshold(s) to ${updatedUrlCount} URL${
        updatedUrlCount === 1 ? '' : 's'
      }.`
    });
  }

  if (skipped.length > 0) {
    const prefix = applied.length > 0 ? '' : 'Nothing applied — ';
    notifications.push({
      type: hasOnlyThresholdFloorSkips(skipped) ? 'info' : 'warning',
      message: `${prefix}${formatThresholdApplySkippedSummary(skipped)}.`
    });
  } else if (applied.length === 0) {
    notifications.push({
      type: 'warning',
      message:
        response.matched_urls === 0
          ? 'No matching URLs found.'
          : response.message || 'Nothing was applied.'
    });
  }

  return notifications;
}

export function getApiErrorMessage(error, fallback) {
  const data = error && error.response && error.response.data;
  const status = error && error.response && error.response.status;
  if (typeof data === 'string' && data) return data;

  if (status === 401 || status === 403) {
    return (data && (data.message || data.error)) || 'Not authorized for this action.';
  }

  if (status === 422 && data && data.errors && typeof data.errors === 'object') {
    const messages = Object.keys(data.errors).reduce((all, key) => {
      const values = Array.isArray(data.errors[key]) ? data.errors[key] : [data.errors[key]];
      values.forEach((value) => {
        if (value) all.push(String(value));
      });
      return all;
    }, []);
    if (messages.length) return messages.slice(0, 3).join(' ');
  }

  if (data && (data.message || data.error)) {
    return data.message || data.error;
  }
  if (status === 422) return 'Validation error.';
  if (error && !error.response) {
    if (error && error.message && error.message !== 'Network Error') {
      return error.message;
    }
    return 'Network error. Please check your connection and try again.';
  }
  return fallback || 'Request failed. Please try again.';
}
